/**
 * Browser-safe canvas assertion helpers for in-sandbox test execution.
 *
 * These utilities operate on live `HTMLCanvasElement` instances (or raw
 * `ImageData`) using only browser-native APIs — no Node.js dependencies.
 *
 * Designed to be used by LLM-generated tests that verify rendering output:
 *   - Board grids are drawn (lines detected)
 *   - Canvas is not blank/single-color
 *   - Specific colors are present after rendering
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface PixelStats {
  /** Total pixel count in the sampled region. */
  totalPixels: number;
  /** Number of unique colors found (quantized to 6-bit per channel). */
  uniqueColors: number;
  /** The single most common color as `[r, g, b, a]`. */
  dominantColor: [number, number, number, number];
  /** Fraction (0–1) of pixels matching the dominant color. */
  dominantFraction: number;
  /** True when >99% of pixels share the same color. */
  isBlank: boolean;
}

export interface ColorPresence {
  /** Hex color that was searched for, e.g. `'#ff0000'`. */
  color: string;
  /** Number of pixels that matched within tolerance. */
  matchedPixels: number;
  /** Fraction (0–1) of total pixels that matched. */
  fraction: number;
}

export interface LineSegment {
  /** Start x (canvas coordinates). */
  x1: number;
  /** Start y (canvas coordinates). */
  y1: number;
  /** End x (canvas coordinates). */
  x2: number;
  /** End y (canvas coordinates). */
  y2: number;
  /** `'horizontal'` or `'vertical'`. */
  orientation: 'horizontal' | 'vertical';
  /** Length in pixels. */
  length: number;
}

export interface LineDetectionResult {
  /** Horizontal lines found. */
  horizontal: LineSegment[];
  /** Vertical lines found. */
  vertical: LineSegment[];
  /** Total lines found. */
  totalLines: number;
}

export interface GridDetectionResult {
  /** Whether a grid pattern was detected. */
  detected: boolean;
  /** Number of horizontal lines forming the grid. */
  horizontalLines: number;
  /** Number of vertical lines forming the grid. */
  verticalLines: number;
  /** Rows implied by the grid (horizontalLines + 1). */
  rows: number;
  /** Columns implied by the grid (verticalLines + 1). */
  cols: number;
}

// ── Readback: any canvas, 2d or WebGL, and the one rule for "black" ──────────

/** A pixel whose Rec. 709 luma is at or below this counts as pure black (an unrendered canvas is 0–5; dark map art sits at 12–15). */
export const LUMA_BLACK_MAX = 8;
/** The default ceiling on the pure-black fraction of a rendering canvas (a menu is calibrated per game; a world is far below this). */
export const DEFAULT_RENDER_BLACK_LIMIT = 0.5;
/** Void detection needs no fidelity: the probe a canvas is downsampled into. */
export const RENDER_PROBE_SIZE = { width: 96, height: 54 } as const;

/**
 * Fraction of pixels at or below `lumaMax` luma. `pixels` is anything with a `data` array of RGBA bytes, four per
 * pixel — `ImageData`, `PixelData` (test-utils' PNG decoder), or raw bytes wrapped as `{ data: bytes }`; an empty
 * buffer is all black (nothing was drawn). The rule reads R, G and B only and ignores alpha: a fully transparent
 * pixel is black by it — so a game that draws on a transparent canvas over a CSS background fails the gate.
 */
export function blackFraction(pixels: { data: ArrayLike<number> }, lumaMax = LUMA_BLACK_MAX): number {
  const d = pixels.data;
  let black = 0;
  for (let i = 0; i < d.length; i += 4) {
    if (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2] <= lumaMax) black++;
  }
  return d.length ? black / (d.length / 4) : 1;
}

/** A rect of a canvas in canvas pixels. */
type PixelRect = { x: number; y: number; width: number; height: number };

/**
 * Draws `rect` of `canvas` into a fresh 2d probe of `size` and reads the probe back. Never asks `canvas` for a
 * context: a canvas's first `getContext` call CREATES one, so asking could turn a game's not-yet-initialised
 * WebGL canvas into a 2d one for good. `null` when it cannot read.
 */
function readThroughProbe(canvas: HTMLCanvasElement, rect: PixelRect, size: { width: number; height: number }): ImageData | null {
  if (!canvas.width || !canvas.height || !canvas.isConnected || typeof document === 'undefined') return null;
  const probe = document.createElement('canvas');
  probe.width = size.width;
  probe.height = size.height;
  const ctx = probe.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  try {
    ctx.drawImage(canvas, rect.x, rect.y, rect.width, rect.height, 0, 0, size.width, size.height);
    // A tainted canvas draws fine and then refuses the read: that is unreadable too.
    return ctx.getImageData(0, 0, size.width, size.height);
  } catch {
    return null;
  }
}

/**
 * Pixels of a canvas — a 2d OR a WebGL one — read through a 2d probe (`drawImage`), optionally a fractional
 * region (0..1 of the canvas) and a probe size (default `RENDER_PROBE_SIZE`). `null` when the canvas cannot be
 * read — zero-size, detached from the document, or the read itself failed (no 2d probe, a tainted canvas): the
 * caller must report that distinctly, never as black. It never asks the canvas for a context, so a LOST WebGL
 * canvas reads back what Chromium gives for one — the last frame read back from it while live, else fully
 * transparent; `isContextLost`, or the canvas's `webglcontextlost` event, tells.
 * A WebGL canvas's drawing buffer is cleared once its frame is presented unless it was created with
 * `preserveDrawingBuffer` — measured in headless Chromium, an on-screen canvas then reads back fully transparent
 * between frames and at the start of the next tick — so read it inside the client's tick
 * (`env.onTick(..., 'after')`) instead: that runs after the systems' `onUpdate` and before the game file's
 * `onAfterUpdate`, so a frame drawn in `onAfterUpdate` still needs `preserveDrawingBuffer`.
 */
export function readCanvasPixels(
  canvas: HTMLCanvasElement,
  region: { x: number; y: number; w: number; h: number } | null = null,
  size: { width: number; height: number } = RENDER_PROBE_SIZE,
): ImageData | null {
  const rect = region
    ? {
      x: Math.floor(region.x * canvas.width),
      y: Math.floor(region.y * canvas.height),
      width: Math.max(1, Math.floor(region.w * canvas.width)),
      height: Math.max(1, Math.floor(region.h * canvas.height)),
    }
    : { x: 0, y: 0, width: canvas.width, height: canvas.height };
  return readThroughProbe(canvas, rect, size);
}

/**
 * True when the canvas holds a WebGL context that has been LOST (one cause: Chromium keeps at most 16 WebGL contexts
 * live and evicts the oldest beyond that). A lost canvas reads back the last frame read back from it while live, else fully
 * transparent, so its pixels cannot tell a lost canvas from a live or an undrawn one — this can, as can the
 * canvas's own `webglcontextlost` event.
 * It asks for `'2d'` first: a canvas's first `getContext` call creates a context of that kind, and a 2d one is
 * never a GPU context — but a canvas with no context yet comes out of this holding a 2d one, which a WebGL game
 * that initialises later can then never replace. Ask it only about a canvas that has shown paint (any pixel with
 * alpha > 0: such a canvas already holds a context) — `env.expectRendering` asks about no other. A canvas whose
 * control went to an OffscreenCanvas refuses every `getContext`: it is not known to be lost, so this answers `false`.
 */
export function isContextLost(canvas: HTMLCanvasElement): boolean {
  try {
    if (canvas.getContext('2d')) return false;
    const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null;
    return !!gl && gl.isContextLost();
  } catch {
    return false;
  }
}

/**
 * A canvas's pixels at FULL resolution — the whole canvas, or `rect` (canvas pixels) of it — for pixel
 * assertions: a 2d canvas through its own context, wherever it is; any other canvas — a WebGL one, or one whose
 * control went to an OffscreenCanvas — through a 2d probe of the rect's own size, which reads only a canvas in
 * the document, as `readCanvasPixels` does. Throws `<label>: the canvas is not readable (detached, zero-size or
 * context lost)` rather than hand an assertion an unreadable canvas as black. Unlike `readCanvasPixels` it asks
 * the canvas for its `'2d'` context first, as every canvas assertion always has: on a canvas with no context yet
 * that CREATES a 2d one. A WebGL canvas without `preserveDrawingBuffer` is only guaranteed readable inside the
 * client's tick, as for `readCanvasPixels`.
 */
export function readCanvasImageData(canvas: HTMLCanvasElement, rect: PixelRect | null = null, label = 'canvas-assertions'): ImageData {
  const { x, y, width, height } = rect ?? { x: 0, y: 0, width: canvas.width, height: canvas.height };
  const ctx = ownContext2d(canvas);
  if (ctx) return ctx.getImageData(x, y, width, height);
  // No 2d context: the canvas holds another kind (or was transferred offscreen), so asking about loss creates nothing.
  const pixels = isContextLost(canvas) ? null : readThroughProbe(canvas, { x, y, width, height }, { width, height });
  if (!pixels) throw new Error(`${label}: the canvas is not readable (detached, zero-size or context lost)`);
  return pixels;
}

// ── Internal helpers ─────────────────────────────────────────────────────────

/** The canvas's 2d context, or `null` — a canvas whose control went to an OffscreenCanvas throws on `getContext`, and has none. */
function ownContext2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  try {
    return canvas.getContext('2d');
  } catch {
    return null;
  }
}

function getImageData(source: HTMLCanvasElement | ImageData): ImageData {
  if (source instanceof ImageData) return source;
  return readCanvasImageData(source);
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  if (h.length === 3) {
    return [
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16),
    ];
  }
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  return Math.max(Math.abs(r1 - r2), Math.abs(g1 - g2), Math.abs(b1 - b2));
}

/** Quantize an RGB triplet to a 6-bit-per-channel key for fast bucketing. */
function quantizeKey(r: number, g: number, b: number): number {
  return ((r >> 2) << 12) | ((g >> 2) << 6) | (b >> 2);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Gather basic pixel statistics for a canvas or ImageData.
 *
 * Useful for quick "is this blank?" checks or understanding the color
 * distribution before writing more specific assertions.
 */
export function getPixelStats(source: HTMLCanvasElement | ImageData): PixelStats {
  const img = getImageData(source);
  const { data, width, height } = img;
  const totalPixels = width * height;

  const buckets = new Map<number, number>();
  let maxCount = 0;
  let maxKey = 0;

  for (let i = 0; i < data.length; i += 4) {
    const key = quantizeKey(data[i], data[i + 1], data[i + 2]);
    const count = (buckets.get(key) ?? 0) + 1;
    buckets.set(key, count);
    if (count > maxCount) {
      maxCount = count;
      maxKey = key;
    }
  }

  // Find the actual average color for the dominant bucket
  const dr = ((maxKey >> 12) & 0x3f) << 2;
  const dg = ((maxKey >> 6) & 0x3f) << 2;
  const db = (maxKey & 0x3f) << 2;

  return {
    totalPixels,
    uniqueColors: buckets.size,
    dominantColor: [dr, dg, db, 255],
    dominantFraction: maxCount / totalPixels,
    isBlank: maxCount / totalPixels > 0.99,
  };
}

/**
 * Assert that a canvas is NOT blank (not a single solid color).
 *
 * Throws an `Error` with a descriptive message if >99% of pixels share
 * the same quantized color.
 *
 * ```ts
 * import { assertCanvasNotBlank } from '@gotti/test-utils';
 * assertCanvasNotBlank(canvas); // throws if canvas is all one color
 * ```
 */
export function assertCanvasNotBlank(
  source: HTMLCanvasElement | ImageData,
  message?: string,
): void {
  const stats = getPixelStats(source);
  if (stats.isBlank) {
    const [r, g, b] = stats.dominantColor;
    throw new Error(
      message ??
        `Canvas is blank: ${stats.dominantFraction * 100}% of pixels are rgb(${r},${g},${b}). ` +
        `Expected meaningful rendering with multiple colors. ` +
        `(${stats.uniqueColors} unique quantized colors found)`,
    );
  }
}

/**
 * Check whether a specific color (hex) is present on the canvas.
 *
 * Returns a `ColorPresence` object with the match count and fraction.
 * Use `tolerance` (per-channel, default 10) to handle anti-aliasing.
 *
 * ```ts
 * const presence = getColorPresence(canvas, '#ffffff', 15);
 * expect(presence.fraction).toBeGreaterThan(0.01); // at least 1% white
 * ```
 */
export function getColorPresence(
  source: HTMLCanvasElement | ImageData,
  colorHex: string,
  tolerance = 10,
): ColorPresence {
  const [tr, tg, tb] = hexToRgb(colorHex);
  const img = getImageData(source);
  const { data, width, height } = img;
  const total = width * height;
  let matched = 0;

  for (let i = 0; i < data.length; i += 4) {
    if (colorDistance(data[i], data[i + 1], data[i + 2], tr, tg, tb) <= tolerance) {
      matched++;
    }
  }

  return { color: colorHex, matchedPixels: matched, fraction: matched / total };
}

/**
 * Assert that a specific hex color occupies at least `minFraction` of the canvas.
 *
 * ```ts
 * assertColorPresent(canvas, '#ffffff', 0.01); // at least 1% white pixels
 * ```
 */
export function assertColorPresent(
  source: HTMLCanvasElement | ImageData,
  colorHex: string,
  minFraction = 0.005,
  tolerance = 10,
): void {
  const presence = getColorPresence(source, colorHex, tolerance);
  if (presence.fraction < minFraction) {
    throw new Error(
      `Color ${colorHex} not sufficiently present: ${(presence.fraction * 100).toFixed(2)}% ` +
      `(${presence.matchedPixels} pixels) — expected at least ${(minFraction * 100).toFixed(2)}%. ` +
      `Tolerance: ${tolerance}`,
    );
  }
}

/**
 * Detect straight horizontal and vertical lines on a canvas.
 *
 * Scans for contiguous runs of non-background pixels along rows (horizontal)
 * and columns (vertical). A run must be at least `minLength` pixels to count.
 *
 * `bgTolerance` controls how close a pixel must be to the dominant/background
 * color to be considered "background" (default 15).
 *
 * ```ts
 * const lines = detectLines(canvas, { minLength: 50 });
 * expect(lines.horizontal.length).toBeGreaterThanOrEqual(2); // grid lines
 * ```
 */
export function detectLines(
  source: HTMLCanvasElement | ImageData,
  options: {
    /** Minimum contiguous pixel run to count as a line. Default: 30 */
    minLength?: number;
    /** Per-channel tolerance for background detection. Default: 15 */
    bgTolerance?: number;
    /** Maximum line thickness to consider (avoids detecting filled regions). Default: 8 */
    maxThickness?: number;
  } = {},
): LineDetectionResult {
  const { minLength = 30, bgTolerance = 15, maxThickness = 8 } = options;
  const img = getImageData(source);
  const { data, width, height } = img;

  // Determine background color (most common)
  const stats = getPixelStats(img);
  const [bgR, bgG, bgB] = stats.dominantColor;

  const isForeground = (i: number) =>
    colorDistance(data[i], data[i + 1], data[i + 2], bgR, bgG, bgB) > bgTolerance;

  const horizontal: LineSegment[] = [];
  const vertical: LineSegment[] = [];

  // Scan rows for horizontal lines
  for (let y = 0; y < height; y++) {
    let runStart = -1;
    for (let x = 0; x <= width; x++) {
      const idx = (y * width + x) * 4;
      const fg = x < width && isForeground(idx);
      if (fg && runStart < 0) {
        runStart = x;
      } else if (!fg && runStart >= 0) {
        const len = x - runStart;
        if (len >= minLength) {
          horizontal.push({
            x1: runStart, y1: y, x2: x - 1, y2: y,
            orientation: 'horizontal', length: len,
          });
        }
        runStart = -1;
      }
    }
  }

  // Scan columns for vertical lines
  for (let x = 0; x < width; x++) {
    let runStart = -1;
    for (let y = 0; y <= height; y++) {
      const idx = (y * width + x) * 4;
      const fg = y < height && isForeground(idx);
      if (fg && runStart < 0) {
        runStart = y;
      } else if (!fg && runStart >= 0) {
        const len = y - runStart;
        if (len >= minLength) {
          vertical.push({
            x1: x, y1: runStart, x2: x, y2: y - 1,
            orientation: 'vertical', length: len,
          });
        }
        runStart = -1;
      }
    }
  }

  // Collapse thick lines: merge lines within maxThickness pixels of each other
  const collapseH = collapseLines(horizontal, 'y1', maxThickness);
  const collapseV = collapseLines(vertical, 'x1', maxThickness);

  return {
    horizontal: collapseH,
    vertical: collapseV,
    totalLines: collapseH.length + collapseV.length,
  };
}

/**
 * Merge lines that are within `threshold` pixels along the given axis.
 * Keeps the longest representative of each cluster.
 */
function collapseLines(
  lines: LineSegment[],
  axis: 'x1' | 'y1',
  threshold: number,
): LineSegment[] {
  if (lines.length === 0) return [];
  const sorted = [...lines].sort((a, b) => a[axis] - b[axis]);
  const clusters: LineSegment[][] = [[sorted[0]]];

  for (let i = 1; i < sorted.length; i++) {
    const last = clusters[clusters.length - 1];
    if (sorted[i][axis] - last[last.length - 1][axis] <= threshold) {
      last.push(sorted[i]);
    } else {
      clusters.push([sorted[i]]);
    }
  }

  // Pick the longest line from each cluster
  return clusters.map(cluster =>
    cluster.reduce((best, line) => (line.length > best.length ? line : best)),
  );
}

/**
 * Detect a grid pattern (N×M) on the canvas.
 *
 * Looks for evenly-spaced horizontal and vertical lines that span
 * a significant portion of the canvas. A 3×3 tic-tac-toe board
 * should produce 2 horizontal + 2 vertical lines.
 *
 * ```ts
 * const grid = detectGrid(canvas);
 * expect(grid.detected).toBe(true);
 * expect(grid.rows).toBe(3);
 * expect(grid.cols).toBe(3);
 * ```
 */
export function detectGrid(
  source: HTMLCanvasElement | ImageData,
  options: {
    /** Minimum fraction of canvas width a horizontal line must span. Default: 0.5 */
    minSpanFraction?: number;
    /** Minimum contiguous pixel run. Default: 30 */
    minLength?: number;
    /** Per-channel tolerance for background detection. Default: 15 */
    bgTolerance?: number;
  } = {},
): GridDetectionResult {
  const { minSpanFraction = 0.5, minLength = 30, bgTolerance = 15 } = options;
  const img = getImageData(source);
  const { width, height } = img;

  const lines = detectLines(img, { minLength, bgTolerance });

  // Filter to lines that span a significant portion of the canvas
  const minHLen = width * minSpanFraction;
  const minVLen = height * minSpanFraction;

  const hLines = lines.horizontal.filter(l => l.length >= minHLen);
  const vLines = lines.vertical.filter(l => l.length >= minVLen);

  const detected = hLines.length >= 1 && vLines.length >= 1;

  return {
    detected,
    horizontalLines: hLines.length,
    verticalLines: vLines.length,
    rows: hLines.length + 1,
    cols: vLines.length + 1,
  };
}

/**
 * Create a test canvas with the given dimensions.
 *
 * Useful when the test harness doesn't wire a canvas automatically
 * and you need one for rendering assertions.
 *
 * ```ts
 * const canvas = createTestCanvas(300, 300);
 * const ctx = canvas.getContext('2d')!;
 * // Invoke system's draw method manually
 * system.draw(ctx, canvas.width, canvas.height);
 * assertCanvasNotBlank(canvas);
 * ```
 */
export function createTestCanvas(
  width = 300,
  height = 300,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}
